// ============================================================
// notifications/notificationsData.ts — the V2 Notifications feed.
//
// PHASE_2M P4: "Scheduled" became Notifications. This is a REAL aggregation —
// every item comes from a live backend the operator already owns:
//   Messages  — sq_communication_channels + DMs (comms spine, live)
//   Tasks     — sq_team_tasks (live)
//   Calls     — Daily meetings + provider status (/api/square/daily/*)
//   Meetings  — sq_daily_meetings (operations Daily Meetings record, live)
//
// NOTHING is fabricated: no seeded rows, no fake unread counts, no fake
// urgency. "Recent" is derived from real timestamps (< 24h). "Mark messages
// read" is the REAL read-cursor write (POST action=mark_read per channel);
// there is NO per-item read state for tasks/calls/meetings — those columns
// deliberately do not offer a fake mark-read.
// ============================================================

import { COMMAND_CENTER_ROOT, commandCenterPath } from '../commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import { api, isAuthError, type Meeting, type DailyStatus } from '../../../api/client'
import { listChannels, listDms, listTeamTasks, markChannelRead, humanizeError, type Channel, type TeamTask } from '../comms/commsData'
import { listDailyMeetings, type DailyMeetingRecord } from '../operations/daily-meetings/dailyMeetings'

export const NOTIFICATIONS_ROOT = commandCenterPath('notifications')

export type NotifChannelKey = 'messages' | 'tasks' | 'calls' | 'meetings'

export interface NotifItem {
  id: string
  title: string
  preview: string
  /** ISO timestamp the item is ranked by (real, from the record). */
  at: string | null
  /** Real recency: the ranked timestamp is < 24h old. */
  recent: boolean
  /** Real urgency: blocked task / urgent priority / follow-up-needed meeting / active call. */
  urgent: boolean
  /** V2 route this item opens. */
  to: string
}

export interface NotifColumnState {
  items: NotifItem[]
  loading: boolean
  /** Honest failure (humanized); null when the load worked. */
  error: string | null
  authRequired: boolean
}

export interface NotificationsFeedState {
  messages: NotifColumnState
  tasks: NotifColumnState
  calls: NotifColumnState & { providerConfigured: boolean | null; dailyStatus: DailyStatus | null }
  meetings: NotifColumnState
}

const EMPTY: NotifColumnState = { items: [], loading: true, error: null, authRequired: false }

const DAY_MS = 24 * 60 * 60 * 1000
export function isRecent(iso: string | null | undefined, now: number): boolean {
  if (!iso) return false
  const t = Date.parse(iso)
  return Number.isFinite(t) && now - t < DAY_MS && t <= now + DAY_MS // tolerate near-future (due soon)
}

export function relTime(iso: string | null | undefined, now: number): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const d = now - t
  const min = Math.round(Math.abs(d) / 60000)
  const label = min < 60 ? `${Math.max(1, min)} min` : min < 60 * 24 ? `${Math.round(min / 60)} hr` : `${Math.round(min / (60 * 24))} d`
  return d >= 0 ? `${label} ago` : `in ${label}`
}

// ---- real mappers -----------------------------------------------------------

export function channelToItem(c: Channel, now: number): NotifItem {
  const isDm = c.kind === 'dm'
  return {
    id: `ch-${c.id}`,
    title: isDm ? `DM — ${c.name}` : `#${c.name}`,
    preview: c.description || (isDm ? 'Direct message thread' : `${c.visibility} channel`),
    at: c.updated_at,
    recent: isRecent(c.updated_at, now),
    urgent: false, // no real urgency signal on a channel row — never invented
    to: `${COMMAND_CENTER_ROOT}/communications/${isDm ? 'dms' : 'channels'}/${c.id}`,
  }
}

export function taskToItem(t: TeamTask, now: number): NotifItem {
  return {
    id: `task-${t.id}`,
    title: t.title,
    preview: t.status === 'blocked' && t.blocker_reason ? `Blocked — ${t.blocker_reason}` : `${t.status.replace('_', ' ')} · ${t.priority}`,
    at: t.due_at || t.updated_at,
    recent: isRecent(t.created_at, now) || isRecent(t.due_at, now),
    urgent: t.status === 'blocked' || t.priority === 'urgent',
    to: `${COMMAND_CENTER_ROOT}/team/tasks/${t.id}`,
  }
}

export function callToItem(m: Meeting, now: number): NotifItem {
  return {
    id: `call-${m.id}`,
    title: m.title,
    preview: `${m.status} · room ${m.daily_room_name}`,
    at: m.starts_at || m.created_at,
    recent: isRecent(m.starts_at || m.created_at, now),
    urgent: m.status === 'active',
    to: commandCenterPath('communications/conference-calls'),
  }
}

export function dailyMeetingToItem(m: DailyMeetingRecord, now: number): NotifItem {
  return {
    id: `dm-${m.id}`,
    title: m.meeting_title,
    preview: `${m.meeting_type} · ${m.meeting_date}${m.follow_up_needed ? ' · follow-up needed' : ''}`,
    at: m.meeting_date ? `${m.meeting_date}T12:00:00Z` : m.updated_at,
    recent: isRecent(m.updated_at, now),
    urgent: !!m.follow_up_needed,
    to: commandCenterPath('operations/daily-meetings'),
  }
}

// ---- the feed hook ----------------------------------------------------------

export function useNotificationsFeed() {
  const [feed, setFeed] = useState<NotificationsFeedState>({
    messages: EMPTY, tasks: EMPTY, calls: { ...EMPTY, providerConfigured: null, dailyStatus: null }, meetings: EMPTY,
  })
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [markState, setMarkState] = useState<'idle' | 'working' | 'done' | 'failed'>('idle')

  const load = useCallback(async (signal?: AbortSignal) => {
    const now = Date.now()
    const settle = <K extends NotifChannelKey>(key: K, patch: Partial<NotificationsFeedState[K]>) =>
      setFeed((f) => ({ ...f, [key]: { ...f[key], loading: false, ...patch } }))
    const fail = (key: NotifChannelKey, err: unknown) =>
      isAuthError(err) ? settle(key, { authRequired: true, error: null }) : settle(key, { error: humanizeError(err) })

    // Four independent real loads — one failing column never fakes another.
    await Promise.all([
      (async () => {
        try {
          const [channels, dms] = await Promise.all([listChannels(signal), listDms(signal)])
          const items = [...channels, ...dms]
            .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
            .slice(0, 8).map((c) => channelToItem(c, now))
          setChannelIds([...channels, ...dms].map((c) => c.id))
          settle('messages', { items, error: null })
        } catch (err) { fail('messages', err) }
      })(),
      (async () => {
        try {
          const tasks = await listTeamTasks(signal)
          const items = tasks
            .filter((t) => t.status !== 'done')
            .sort((a, b) => Number(b.status === 'blocked') - Number(a.status === 'blocked') || (b.updated_at || '').localeCompare(a.updated_at || ''))
            .slice(0, 8).map((t) => taskToItem(t, now))
          settle('tasks', { items, error: null })
        } catch (err) { fail('tasks', err) }
      })(),
      (async () => {
        try {
          const [listRes, status] = await Promise.all([
            api.commandCenter.meetings.list(),
            api.commandCenter.meetings.status().catch(() => null),
          ])
          const items = (listRes.meetings || [])
            .filter((m) => m.status !== 'cancelled' && m.status !== 'ended')
            .slice(0, 8).map((m) => callToItem(m, now))
          settle('calls', { items, error: null, providerConfigured: listRes.provider_configured ?? null, dailyStatus: (status as any)?.status ?? status ?? null })
        } catch (err) { fail('calls', err) }
      })(),
      (async () => {
        try {
          const meetings = await listDailyMeetings(signal)
          const items = meetings.slice(0, 8).map((m) => dailyMeetingToItem(m, now))
          settle('meetings', { items, error: null })
        } catch (err) { fail('meetings', err) }
      })(),
    ])
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    setFeed({ messages: EMPTY, tasks: EMPTY, calls: { ...EMPTY, providerConfigured: null, dailyStatus: null }, meetings: EMPTY })
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  /** REAL mark-read: writes the read cursor on every channel/DM the caller belongs to. */
  const markMessagesRead = useCallback(async () => {
    if (channelIds.length === 0) return
    setMarkState('working')
    try {
      await Promise.all(channelIds.map((id) => markChannelRead(id)))
      setMarkState('done')
    } catch {
      setMarkState('failed') // honest failure — never a fake success
    }
  }, [channelIds])

  return { feed, reload: () => void load(), markMessagesRead, markState, markableChannels: channelIds.length }
}
